import { Link } from "react-router-dom";

export type DashStats = {
  totalClients: number;
  newLeads: number;
  activeContracts: number;
  totalCollected: number;
  activeJobs: number;
  pendingSelections: number;
  pendingDeliveries: number;
  pendingPayments: number;
};

interface Props {
  stats: DashStats;
  modules: Record<string, boolean>;
}

export default function DashboardStats({ stats, modules }: Props) {
  const cards = [
    {
      key: "clients",
      always: true,
      label: "Total Clients",
      value: stats.totalClients,
      sub: "active in studio",
      icon: "ri-user-3-line",
      color: "text-teal-600",
      bg: "bg-teal-50",
      border: "border-teal-200",
      href: "/admin/clients",
    },
    {
      key: "leads",
      always: true,
      label: "New Leads",
      value: stats.newLeads,
      sub: "awaiting contact",
      icon: "ri-user-received-line",
      color: "text-[#a6853e]",
      bg: "bg-[#C8A96E]/10",
      border: "border-[#C8A96E]/40",
      href: "/admin/leads",
      highlight: stats.newLeads > 0,
    },
    {
      key: "contracts",
      always: true,
      label: "Active Contracts",
      value: stats.activeContracts,
      sub: "signed or in progress",
      icon: "ri-file-text-line",
      color: "text-slate-700",
      bg: "bg-slate-100",
      border: "border-slate-300",
      href: "/admin/contracts",
    },
    {
      key: "payments",
      always: true,
      label: "Revenue Collected",
      value: `$${stats.totalCollected.toLocaleString()}`,
      sub: `${stats.pendingPayments} payment${stats.pendingPayments !== 1 ? "s" : ""} pending`,
      icon: "ri-money-dollar-circle-line",
      color: "text-emerald-600",
      bg: "bg-emerald-50",
      border: "border-emerald-200",
      href: "/admin/payments",
      highlight: stats.pendingPayments > 0,
    },
    {
      key: "jobs",
      always: true,
      label: "Active Jobs",
      value: stats.activeJobs,
      sub: "in workflow",
      icon: "ri-camera-line",
      color: "text-amber-600",
      bg: "bg-amber-50",
      border: "border-amber-200",
      href: "/admin/jobs",
    },
    {
      key: "photo_selection",
      always: false,
      moduleId: "photo_selection",
      label: "Pending Selections",
      value: stats.pendingSelections,
      sub: "awaiting client review",
      icon: "ri-image-2-line",
      color: "text-rose-600",
      bg: "bg-rose-50",
      border: "border-rose-200",
      href: "/admin/jobs",
      highlight: stats.pendingSelections > 0,
    },
    {
      key: "final_deliveries",
      always: false,
      moduleId: "final_deliveries",
      label: "Pending Deliveries",
      value: stats.pendingDeliveries,
      sub: "ready to send",
      icon: "ri-send-plane-line",
      color: "text-teal-600",
      bg: "bg-teal-50",
      border: "border-teal-200",
      href: "/admin/jobs",
      highlight: stats.pendingDeliveries > 0,
    },
  ];

  const visible = cards.filter((c) => c.always || (c.moduleId && modules[c.moduleId]));

  const cols =
    visible.length <= 4
      ? "grid-cols-2 sm:grid-cols-4"
      : visible.length === 5
      ? "grid-cols-2 sm:grid-cols-3 lg:grid-cols-5"
      : "grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7";

  return (
    <div className={`grid gap-4 ${cols}`}>
      {visible.map((card) => (
        <Link
          key={card.key}
          to={card.href}
          className={`group relative bg-white rounded-xl p-5 border transition-all cursor-pointer ${
            card.highlight
              ? `${card.border} hover:opacity-90`
              : "border-slate-200 hover:border-slate-300"
          }`}
        >
          {card.highlight && (
            <span className={`absolute top-3 right-3 w-2 h-2 rounded-full animate-pulse ${card.color.replace("text-", "bg-")}`}></span>
          )}
          <div className="flex items-center justify-between mb-4">
            <span className="text-slate-500 text-[10px] font-semibold uppercase tracking-wider leading-tight">
              {card.label}
            </span>
            <span className={`w-8 h-8 flex items-center justify-center rounded-lg ${card.bg} ${card.color} shrink-0`}>
              <i className={`${card.icon} text-sm`}></i>
            </span>
          </div>
          <p className={`text-2xl font-bold ${card.color} group-hover:opacity-80 transition-opacity`}>{card.value}</p>
          <p className="text-slate-400 text-xs mt-1 truncate">{card.sub}</p>
        </Link>
      ))}
    </div>
  );
}
